import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { isEmpty, sortBy } from 'lodash'

import { CONVERSATION } from '@/components/transcript/transcript.constants'
import { Breadcrumbs } from '@/components/forms/Breadcrumbs/Breadcrumbs'
import { ProgressBar } from '@/components/navigation/ProgressBar'
import { MarkAsReviewed } from '@/views/Playlists/components/MarkAsReviewed'
import { AddToPlaylist } from '@/views/Playlists/components/AddToPlaylist'
import { ScoreForScorecard } from '@/views/Scorecards/ScoreForScorecard'
import { ManualTaskModal } from '@/views/Calls/components/ManualTaskModal'
import { CopyButton } from '@/components/buttons/CopyButton'

import { DownloadDropdown } from './DownloadDropdown'
import { parseCallExplorerParams } from './helpers'

export const CallExplorerHeader = ({ callId, playlist, metadata }) => {
  const location = useLocation()
  const { callExplorer } = useSelector((state) => state.callSearch)
  const { qa_copilot_enabled: copilotEnabled } = useSelector((state) => state.currentOrganization)
  const {
    compliance_tasks_enabled: complianceTasksEnabled,
    coaching_tasks_enabled: coachingTasksEnabled,
  } = useSelector((state) => state.currentOrganization)
  const { edit_qa_copilot: editQaCopilot } = useSelector((state) => state.currentUser)
  const { mediaPlayer } = useSelector((state) => state.media)
  const { audioStatus, videoStatus, audioError, videoError } = mediaPlayer
  const { associatedCallIds, transcript, postcall_transcript, pretty_transcript } = callExplorer
  const hasConversationBlocks = transcript?.some((block) => block.type === CONVERSATION) ?? false
  const hasNoTranscript =
    !hasConversationBlocks && isEmpty(postcall_transcript) && isEmpty(pretty_transcript)
  // const callDuration = metadata?.call_duration || 0
  const isAudioAvailable = audioStatus === 'available'
  const isVideoAvailable = videoStatus === 'available'
  const { multi_call_scoring_enabled: multiCallScoringEnabled } = useSelector(
    (state) => state.currentOrganization
  )
  const sortedassociatedCallIds = sortBy(associatedCallIds)
  const indexOfViewedCall = sortedassociatedCallIds.indexOf(parseInt(callId, 10))
  const callExplorerLinks = sortedassociatedCallIds.map((id, index) => ({
    slug: `/calls/${id}`,
    label: `Call ${index + 1} of ${sortedassociatedCallIds.length}`,
  }))
  const shouldShowMultiCallScoring = multiCallScoringEnabled && !isEmpty(associatedCallIds)
  const { playlistUuid } = parseCallExplorerParams(location)

  let breadcrumbs = [
    {
      label: 'Calls',
      link: '/explorer',
    },
  ]
  let playlistCall

  if (playlistUuid && !isEmpty(playlist)) {
    if (playlist.type === 'coaching') {
      breadcrumbs = [
        {
          label: 'Coaching',
          link: '/coaching',
          dataTestId: 'breadcrumb-coaching',
        },
      ]
    } else {
      // eslint-disable-next-line eqeqeq
      playlistCall = playlist.playlist_calls.find((call) => call.call_id == callId)
      breadcrumbs = [
        {
          label: 'Playlists',
          link: '/playlists',
          dataTestId: 'breadcrumb-playlists',
        },
        {
          label: playlist.name,
          link: `/playlists/${playlistUuid}?organization_id=${metadata.organization_id}`,
          dataTestId: 'breadcrumb-playlist',
        },
      ]
    }
  }

  const currentLink = (
    <div className="flex-align-center small-gap">
      {playlistCall?.alias ? `${playlistCall.alias} (${callId})` : callId}
      <CopyButton content={callId} showTooltip iconOnly />
    </div>
  )

  return (
    <header className="page-header call-explorer-page-header" data-testid="call-explorer-page">
      <div className="flex-align-center medium-gap">
        <Breadcrumbs backLinks={breadcrumbs} currentLink={currentLink} />
        {shouldShowMultiCallScoring && (
          <ProgressBar
            currentPage={indexOfViewedCall + 1}
            totalPages={associatedCallIds.length}
            links={callExplorerLinks}
          />
        )}
      </div>
      <div className="flex-align-center medium-gap">
        <MarkAsReviewed />
        {complianceTasksEnabled && (
          <ManualTaskModal
            callId={callId}
            userId={callExplorer.metadata.agent_id}
            organizationId={callExplorer.metadata.organization_id}
            type="compliance"
          />
        )}
        {coachingTasksEnabled && (
          <ManualTaskModal
            callId={callId}
            userId={callExplorer.metadata.agent_id}
            organizationId={callExplorer.metadata.organization_id}
            type="coaching"
          />
        )}
        {metadata.organization_id && (
          <AddToPlaylist callIds={[callId]} organizationId={metadata.organization_id} />
        )}
        {metadata.organization_id && copilotEnabled && (
          <ScoreForScorecard
            callId={callId}
            organizationId={metadata.organization_id}
            isDisabled={!editQaCopilot || hasNoTranscript}
            disabledTooltip={
              !editQaCopilot ? 'You have read-only access' : 'Transcript required to score call'
            }
          />
        )}
        <DownloadDropdown
          audioError={audioError}
          isAudioAvailable={isAudioAvailable}
          isVideoAvailable={isVideoAvailable}
          videoError={videoError}
        />
      </div>
    </header>
  )
}
